import { type ReactNode } from 'react';

import { SubscriptionPlan } from './subscription.types';

/** Early adopter pricing constants for Littlebird subscription */
export const EARLY_ADOPTER_MONTHLY_PRICE_NUMERIC = 20;
export const EARLY_ADOPTER_YEARLY_PRICE_NUMERIC = 204;

/** Subscription features list */
export type PricingFeature = {
  title: ReactNode;
  description?: string;
};

export const SUBSCRIPTION_FEATURES: PricingFeature[] = [
  {
    title: (
      <span>
        Expanded daily chats
        <span className="text-foreground-muted text-xs">*</span>
      </span>
    ),
    description: 'Get help from your AI assistant whenever you need it',
  },
  {
    title: (
      <span>
        Unlimited meeting notes
        <span className="text-foreground-muted text-xs">*</span>
      </span>
    ),
    description: 'Capture and summarize every conversation automatically',
  },
  {
    title: (
      <span>
        More active routines
        <span className="text-foreground-muted text-xs">*</span>
      </span>
    ),
    description: 'Set prompts to run automatically on your schedule',
  },
  {
    title: 'Early access to new features',
    description: 'Be the first to try our latest tools and updates',
  },
  {
    title: 'Priority email support',
    description: 'Jump the queue with dedicated support',
  },
];

const PLUS_PLAN_FEATURES = [
  'Advanced intelligence in chat',
  'Enhanced memory and personalization',
  'Monthly usage credits for chats and routines',
  'Unlimited meeting notes',
  'Access to max intelligence for complex tasks and deep research',
  'Access to standard image generation',
  'Access to MCP',
  'Priority support',
];

const POWER_PLAN_FEATURES = [
  '2.5× more usage than Plus',
  'Premium image generation',
  'Higher monthly usage credits and research limits',
  'Early access to new features',
];

const getProPlanFeatures = (tierLabel: '5×' | '12×') => [
  `${tierLabel} more usage than Plus`,
  'Auto-detect language in meeting notes',
  'Higher limits for max intelligence',
  'Access to premium image generation',
  'Early access to new features',
];

export const PLAN_FEATURES: Record<SubscriptionPlan, string[]> = {
  [SubscriptionPlan.BASIC]: [],
  [SubscriptionPlan.PLUS_MONTHLY]: PLUS_PLAN_FEATURES,
  [SubscriptionPlan.PLUS_ANNUAL]: PLUS_PLAN_FEATURES,
  [SubscriptionPlan.POWER_MONTHLY]: POWER_PLAN_FEATURES,
  [SubscriptionPlan.POWER_ANNUAL]: POWER_PLAN_FEATURES,
  [SubscriptionPlan.PRO_MONTHLY]: getProPlanFeatures('5×'),
  [SubscriptionPlan.PRO2_MONTHLY]: getProPlanFeatures('12×'),
};
